/**
 * Global Chunk Load Error Recovery
 * 
 * Automatically catches ChunkLoadError (e.g. missing hashed chunks after a new deployment)
 * and performs a one-time clean page reload to fetch the latest index.html and chunks.
 */

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted_at';
const RELOAD_THROTTLE_MS = 20000; // 20-second throttle to prevent infinite reload loops

/**
 * Check if the error is caused by a missing/stale chunk after a new deployment
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  const msg = (error.message || error.reason?.message || error.toString() || '').toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('missing: chunk') ||
    msg.includes('dynamically imported module') ||
    (msg.includes('unexpected token') && msg.includes('<'))
  );
};

/**
 * Attempt a safe auto-reload if the error is a ChunkLoadError
 * @returns {boolean} True if reload was triggered, false otherwise
 */
export const handleChunkLoadError = (error) => {
  if (!isChunkLoadError(error)) return false;

  try {
    const lastAttempt = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);
    const now = Date.now();

    if (now - lastAttempt > RELOAD_THROTTLE_MS) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toString());
      console.warn('[ChunkLoader] Stale bundle chunk detected. Reloading for latest version...', error);
      window.location.reload();
      return true;
    }
  } catch (e) {
    window.location.reload();
    return true;
  }

  return false;
};

/**
 * Initialize global error and unhandledrejection listeners for chunk recovery
 */
export const initChunkErrorRecovery = () => {
  if (typeof window === 'undefined') return;

  // Window error listener (catches <script> tag errors and synchronous chunk load errors)
  window.addEventListener('error', (event) => {
    if (handleChunkLoadError(event?.error || event)) {
      event.preventDefault();
    }
  });

  // Unhandled promise rejection listener (catches dynamic import() failures in React.lazy)
  window.addEventListener('unhandledrejection', (event) => {
    if (handleChunkLoadError(event?.reason)) {
      event.preventDefault();
    }
  });
};
